import { useRequiredContext } from "@redotech/react-util/context";
import { EffectApiClientContext } from "@redotech/redo-merchant-app-common/effect-api-client-provider";
import {
  ALL_COUNT_STATUS_VALUES,
  buildStatusCountViews,
  encodeSupportTicketCountFilterGroup,
} from "@redotech/redo-support-v2/query/support-ticket-count-query";
import { activeViewStatusCountsQueryKey } from "@redotech/redo-support-v2/query/support-ticket-query-keys";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { catchApiErrors } from "../db/error-handling";
import { useQueryOptions } from "../filters/filters-context";
import type { StatusValue } from "../filters/support-tickets-filter-toolbar";
import { useSupportTicketQueryScope } from "./use-support-ticket-query-scope";

export type StatusCounts = Record<StatusValue | "all", number>;

export function useActiveViewStatusCounts(): StatusCounts {
  const { filterGroup } = useQueryOptions();
  const effectApi = useRequiredContext(EffectApiClientContext);
  const queryScope = useSupportTicketQueryScope();

  const encodedFilterGroupKey = useMemo(() => {
    return encodeSupportTicketCountFilterGroup(filterGroup) ?? "";
  }, [filterGroup]);

  const { data } = useQuery<StatusCounts>({
    queryKey: activeViewStatusCountsQueryKey(encodedFilterGroupKey, queryScope),
    queryFn: async (): Promise<StatusCounts> => {
      const views = buildStatusCountViews(filterGroup);

      const client = effectApi.getClient();
      const result = await effectApi.runPromise(
        catchApiErrors(
          client.supportTicketCounts.getSupportTicketCounts({
            payload: { views },
          }),
        ),
      );

      const counts = result as Record<string, number>;
      // Use ALL_COUNT_STATUS_VALUES which excludes ARCHIVED to match legacy behavior
      const allCount = ALL_COUNT_STATUS_VALUES.reduce(
        (sum, s) => sum + (counts[s] ?? 0),
        0,
      );

      return {
        OPEN: counts.OPEN ?? 0,
        CLOSED: counts.CLOSED ?? 0,
        IN_PROGRESS: counts.IN_PROGRESS ?? 0,
        SNOOZED: counts.SNOOZED ?? 0,
        DRAFT: counts.DRAFT ?? 0,
        all: allCount,
      };
    },
    enabled: !!queryScope.teamId,
    staleTime: 0,
  });

  return (
    data ?? { OPEN: 0, CLOSED: 0, IN_PROGRESS: 0, SNOOZED: 0, DRAFT: 0, all: 0 }
  );
}
